import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import StarIcon from '@mui/icons-material/Star';
import { Alert, Box, Button, Chip, Link, Paper, Stack, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  createContact,
  deleteContact,
  listContactLabels,
  readContact,
  saveContact,
} from '../../api/business/endpoints';
import type { ContactDetail } from '../../api/business/types';
import { useAppMutation } from '../../api/useAppMutation';
import { usePermissions } from '../../auth/usePermissions';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import {
  contactDraftOf,
  contactInputOf,
  websiteHref,
  type ContactDraft,
} from '../../components/contactDraft';
import { ErrorNotice } from '../../components/ErrorNotice';
import { formatDateTime } from '../../components/format';
import { PageHeader } from '../../components/PageHeader';
import { useLanguage } from '../../i18n/LanguageContext';

import { ContactForm } from './ContactForm';
import { ContactHistory } from './ContactHistory';
import { ContactNotes } from './ContactNotes';

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <Paper variant="outlined" sx={{ p: 2.5 }}>
    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
      {title}
    </Typography>
    {children}
  </Paper>
);

const Line = ({ children, caption }: { children: ReactNode; caption?: string | null }) => (
  <Box sx={{ mb: 0.75 }}>
    <Typography variant="body2" component="div" sx={{ overflowWrap: 'anywhere' }}>
      {children}
    </Typography>
    {caption ? (
      <Typography variant="caption" color="text.secondary">
        {caption}
      </Typography>
    ) : null}
  </Box>
);

const AFFECTED: Array<string> = ['contacts', 'contact', 'contact-history', 'contact-labels'];

/**
 * A contact's card (R0039): everything about it at a glance — details, labels, e-mails and phones to click, addresses,
 * people, links, the description — its notes and its history beside; edited whole in place. `contactId` "new" is a
 * new contact.
 */
export const ContactDetailPage = () => {
  const { language, t } = useLanguage();
  const navigate = useNavigate();
  const permissions = usePermissions();
  const { companyId = '', contactId = '' } = useParams();
  const isNew = contactId === 'new';
  const canManage = permissions.can('contacts.manage', companyId);
  const [draft, setDraft] = useState<ContactDraft | null>(isNew ? contactDraftOf(null) : null);
  const [isDeleting, setDeleting] = useState(false);

  const query = useQuery({
    queryKey: ['contact', companyId, contactId],
    queryFn: () => readContact(companyId, contactId),
    enabled: !isNew,
  });
  const labels = useQuery({
    queryKey: ['contact-labels', companyId],
    queryFn: () => listContactLabels(companyId),
    enabled: draft !== null,
  });
  const save = useAppMutation(
    (edited: ContactDraft) =>
      isNew
        ? createContact(companyId, contactInputOf(edited))
        : saveContact(companyId, contactId, contactInputOf(edited)),
    AFFECTED,
    (saved: ContactDetail) => {
      setDraft(null);

      if (isNew) {
        navigate(`/contacts/${companyId}/${saved.id}`, { replace: true });
      }
    },
  );
  const remove = useAppMutation(
    () => deleteContact(companyId, contactId),
    AFFECTED,
    () => navigate('/contacts'),
  );
  const contact = query.data;

  if (draft !== null) {
    return (
      <>
        <PageHeader
          title={isNew ? t('contact.createTitle') : (contact?.name ?? '')}
          action={
            <Stack direction="row" spacing={1}>
              <Button onClick={() => (isNew ? navigate('/contacts') : setDraft(null))}>
                {t('common.cancel')}
              </Button>
              <Button
                variant="contained"
                disabled={save.isPending || draft.name.trim() === ''}
                onClick={() => save.mutate(draft)}
              >
                {isNew ? t('common.create') : t('common.save')}
              </Button>
            </Stack>
          }
        />
        <ErrorNotice error={save.error ?? labels.error} />
        <ContactForm draft={draft} onChange={setDraft} labels={labels.data ?? []} />
        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
          <Button onClick={() => (isNew ? navigate('/contacts') : setDraft(null))}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            disabled={save.isPending || draft.name.trim() === ''}
            onClick={() => save.mutate(draft)}
          >
            {isNew ? t('common.create') : t('common.save')}
          </Button>
        </Stack>
      </>
    );
  }

  return (
    <>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/contacts')} sx={{ mb: 1 }}>
        {t('contacts.title')}
      </Button>
      <ErrorNotice error={query.error} onRetry={() => void query.refetch()} />
      {contact ? (
        <>
          <PageHeader
            title={contact.name}
            subtitle={`${t(`contactKind.${contact.kind}` as const)} · ${t(`contactType.${contact.type}` as const)}`}
            action={
              canManage ? (
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteOutlineIcon />}
                    onClick={() => setDeleting(true)}
                  >
                    {t('contact.delete')}
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<EditOutlinedIcon />}
                    onClick={() => setDraft(contactDraftOf(contact))}
                  >
                    {t('common.edit')}
                  </Button>
                </Stack>
              ) : null
            }
          />
          {contact.doNotContact ? (
            <Alert severity="warning" variant="outlined" icon={<BlockOutlinedIcon />} sx={{ mb: 2 }}>
              {t('contacts.doNotContact')} — {t('contact.doNotContactHint')}
            </Alert>
          ) : null}
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 7 }}>
              <Stack spacing={2}>
                <Section title={t('contact.section.identity')}>
                  <Grid container spacing={1}>
                    {(
                      [
                        ['contact.legalName', contact.legalName],
                        ['contact.ico', contact.ico],
                        ['contact.dic', contact.dic],
                        ['contact.icDph', contact.icDph],
                      ] as const
                    ).map(([key, value]) => (
                      <Grid key={key} size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary">
                          {t(key)}
                        </Typography>
                        <Typography variant="body2">{value ?? t('common.none')}</Typography>
                      </Grid>
                    ))}
                  </Grid>
                  {contact.labels.length > 0 ? (
                    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mt: 1.5 }}>
                      {contact.labels.map((label) => (
                        <Chip key={label.id} size="small" label={label.name} />
                      ))}
                    </Stack>
                  ) : null}
                </Section>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Section title={t('contact.section.emails')}>
                      {contact.emails.length === 0 ? <Line>{t('common.none')}</Line> : null}
                      {contact.emails.map((row) => (
                        <Line key={row.email} caption={[row.label, row.note].filter(Boolean).join(' · ')}>
                          <Link href={`mailto:${row.email}`}>{row.email}</Link>
                          {row.isPrimary ? (
                            <StarIcon
                              color="warning"
                              sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'middle' }}
                            />
                          ) : null}
                        </Line>
                      ))}
                    </Section>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Section title={t('contact.section.phones')}>
                      {contact.phones.length === 0 ? <Line>{t('common.none')}</Line> : null}
                      {contact.phones.map((row) => (
                        <Line key={row.phone} caption={[row.label, row.note].filter(Boolean).join(' · ')}>
                          <Link href={`tel:${row.phone}`}>{row.phone}</Link>
                          {row.isPrimary ? (
                            <StarIcon
                              color="warning"
                              sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'middle' }}
                            />
                          ) : null}
                        </Line>
                      ))}
                    </Section>
                  </Grid>
                </Grid>
                <Section title={t('contact.section.addresses')}>
                  {contact.addresses.length === 0 ? <Line>{t('common.none')}</Line> : null}
                  {contact.addresses.map((row, index) => (
                    <Line key={index} caption={row.note}>
                      <Chip
                        size="small"
                        variant={row.kind === 'BILLING' ? 'filled' : 'outlined'}
                        color={row.kind === 'BILLING' ? 'primary' : 'default'}
                        label={row.kind === 'BILLING' ? t('contact.billing') : t('contact.operating')}
                        sx={{ mr: 1 }}
                      />
                      {[row.street, [row.postalCode, row.city].filter(Boolean).join(' '), row.country]
                        .filter(Boolean)
                        .join(', ')}
                    </Line>
                  ))}
                </Section>
                <Section title={t('contact.section.persons')}>
                  {contact.persons.length === 0 ? <Line>{t('common.none')}</Line> : null}
                  {contact.persons.map((row, index) => (
                    <Line key={index} caption={row.note}>
                      <strong>{row.name}</strong>
                      {row.role ? ` · ${row.role}` : ''}
                      {row.email ? (
                        <>
                          {' · '}
                          <Link href={`mailto:${row.email}`}>{row.email}</Link>
                        </>
                      ) : null}
                      {row.phone ? (
                        <>
                          {' · '}
                          <Link href={`tel:${row.phone}`}>{row.phone}</Link>
                        </>
                      ) : null}
                    </Line>
                  ))}
                </Section>
                <Section title={t('contact.section.websites')}>
                  {contact.websites.length === 0 ? <Line>{t('common.none')}</Line> : null}
                  {contact.websites.map((row) => (
                    <Line key={row.url} caption={t(`websiteKind.${row.kind}` as const)}>
                      <Link href={websiteHref(row.url)} target="_blank" rel="noopener noreferrer">
                        {row.url}
                      </Link>
                    </Line>
                  ))}
                </Section>
                <Section title={t('contact.section.description')}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                    {contact.description ?? t('common.none')}
                  </Typography>
                </Section>
                <Typography variant="caption" color="text.secondary">
                  {t('contact.created', {
                    email: contact.createdByEmail ?? t('contact.someone'),
                    date: formatDateTime(contact.createdAt, language),
                  })}
                  {' · '}
                  {t('contact.updated', {
                    email: contact.updatedByEmail ?? t('contact.someone'),
                    date: formatDateTime(contact.updatedAt, language),
                  })}
                </Typography>
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 5 }}>
              <Stack spacing={2}>
                <ContactNotes companyId={companyId} contactId={contactId} canManage={canManage} />
                <ContactHistory companyId={companyId} contactId={contactId} />
              </Stack>
            </Grid>
          </Grid>
          {isDeleting ? (
            <ConfirmDialog
              title={t('contact.deleteTitle')}
              message={t('contact.deleteMessage', { name: contact.name })}
              confirmLabel={t('contact.delete')}
              onConfirm={() => remove.mutate(undefined)}
              onClose={() => setDeleting(false)}
              isBusy={remove.isPending}
              error={remove.error}
            />
          ) : null}
        </>
      ) : null}
    </>
  );
};
